import { useEffect, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import PikBrand from '../brand/PikBrand';
import { profileShareAPI } from '../../lib/api';

/**
 * Session 126r — "Glenn invited you to join PeopleiKnow" context card
 * shown on /signup when the visitor arrived via a share/invite link.
 * Two reasons it exists:
 *   1. Trust signal — strangers won't sign up for an unfamiliar service,
 *      but they WILL finish a flow that someone they know just started.
 *   2. Reminder — by the time a friend taps the email link, opens the
 *      profile page, taps Add, and gets to the signup form, they often
 *      forget WHY they're signing up. Showing the inviter's name +
 *      photo reanchors them in 2 seconds.
 *
 * Two variants:
 *   • invite — explicit "X sent you an invite" (?from=invite). Stronger
 *     hook + we promise to save them as the first contact.
 *   • share  — softer "X shared their profile with you" (?from=share).
 *     Same auto-save behaviour, gentler copy.
 *
 * The inviter's name is always shown; the photo only appears if the
 * inviter's profile_sharing settings permit photo at the configured
 * share level (we just read the public_view payload and let the
 * server decide).
 */
export default function InviterContextBanner({ shareToken, variant = 'invite' }) {
  const [loading, setLoading] = useState(true);
  const [inviter, setInviter] = useState(null);

  useEffect(() => {
    if (!shareToken) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    profileShareAPI.getByToken(shareToken)
      .then(({ data }) => {
        if (cancelled) return;
        setInviter({
          first_name: (data?.first_name || '').trim(),
          last_name: (data?.last_name || '').trim(),
          photo_url: data?.photo_url || null,
          headline: data?.headline || null,
        });
      })
      .catch(() => {
        // Token went stale or owner deleted the share — we silently
        // skip the banner rather than red-error the signup page.
        if (!cancelled) setInviter(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [shareToken]);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center gap-2 text-xs text-muted-foreground py-3"
        data-testid="inviter-context-loading"
      >
        <Loader2 size={14} className="animate-spin" /> Looking up your inviter…
      </div>
    );
  }

  // No inviter resolved — skip the banner entirely. Page still works.
  if (!inviter || (!inviter.first_name && !inviter.last_name)) return null;

  const displayName = [inviter.first_name, inviter.last_name].filter(Boolean).join(' ').trim();
  const initials = (
    (inviter.first_name?.[0] || '') + (inviter.last_name?.[0] || '')
  ).toUpperCase() || '?';
  const isInvite = variant === 'invite';

  return (
    <div
      className="rounded-2xl border-2 border-nav-blue/20 bg-gradient-to-br from-nav-blue/5 to-emerald-50/40 p-4 flex items-start gap-3"
      data-testid="inviter-context-banner"
    >
      {inviter.photo_url ? (
        <img
          src={inviter.photo_url}
          alt={displayName}
          className="w-14 h-14 rounded-full object-cover shrink-0 ring-2 ring-white shadow"
          data-testid="inviter-context-photo"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      ) : (
        <div
          className="w-14 h-14 rounded-full bg-nav-blue text-white flex items-center justify-center font-heading font-bold text-lg shrink-0 ring-2 ring-white shadow"
          data-testid="inviter-context-initials"
        >
          {initials}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-nav-blue font-semibold">
          <Sparkles size={12} />
          <span>{isInvite ? 'You\u2019ve been invited' : 'Shared with you'}</span>
        </div>
        <p className="font-heading font-bold text-base text-foreground mt-0.5" data-testid="inviter-context-name">
          {displayName}{' '}
          <span className="font-normal text-sm text-foreground/80">
            {isInvite
              ? <>invited you to join <PikBrand /></>
              : <>shared their profile with you on <PikBrand /></>}
          </span>
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {isInvite
            ? `Finish creating your account and we'll save ${inviter.first_name || displayName} as your first contact.`
            : `Sign up and you'll save ${inviter.first_name || displayName} as a contact automatically.`}
        </p>
      </div>
    </div>
  );
}
